export interface User {
  id: string;
  email: string;
  role: string;
  tenant_id: string;
  created_at?: string;
  full_name?: string | null;
  email_verified_at?: string | null;
  signup_source?: string | null;
  require_2fa?: boolean;
}

export interface TenantMembership {
  tenant_id: string;
  tenant_name: string;
  role: string;
  is_active: boolean;
}

export interface MyTenantsResponse {
  tenants: TenantMembership[];
  current_tenant_id: string;
}

export type LoginResponse =
  | { user: User; requires_tenant_selection?: false; requires_2fa?: false }
  | {
      requires_tenant_selection: true;
      requires_2fa?: false;
      pending_token: string;
      tenants: TenantMembership[];
      user?: undefined;
    }
  | {
      requires_2fa: true;
      token: string;
      user?: undefined;
      requires_tenant_selection?: false;
    };

export function isTenantSelectionRequired(res: LoginResponse): res is Extract<LoginResponse, { requires_tenant_selection: true }> {
  return res.requires_tenant_selection === true;
}

export function isTwoFactorRequired(res: LoginResponse): res is Extract<LoginResponse, { requires_2fa: true }> {
  return res.requires_2fa === true;
}

export interface Device {
  id: string;
  name: string;
  topic: string;
  location: string;
  api_key?: string;
  heartbeat_timeout_minutes?: number;
  gateway_topic?: string;
  device_identifier?: string;
  parser_type?: 'direct' | 'devId_field' | 'json_path' | 'custom';
  parser_config?: any;
  mqtt_connection_id?: string;
  pos_x?: number;
  pos_y?: number;
  area_id?: string | null;
  area_name?: string | null;
  site_id?: string | null;
  site_name?: string | null;
  created_at: string;
  status?: 'online' | 'offline';
  last_reading_at?: string;
  maintenance_mode?: boolean;
  latest_battery?: number | null;
  latest_signal_strength?: number | null;
}

export interface Area {
  id: string;
  site_id: string;
  name: string;
  description?: string | null;
  device_count?: number;
  created_at?: string;
}

export interface Site {
  id: string;
  name: string;
  description?: string | null;
  areas: Area[];
  created_at?: string;
}

export interface Reading {
  time: string;
  device_id: string;
  device_name?: string;
  temperature?: number;
  humidity?: number;
  pressure?: number;
  battery?: number;
  signal_strength?: number;
  metadata?: Record<string, any>;
}

export interface Aggregate {
  bucket: string;
  avg: number;
  min: number;
  max: number;
  count: number;
}

export interface Alert {
  id: string;
  tenant_id: string;
  device_id: string;
  name: string;
  metric: string;
  operator: '>' | '<' | '>=' | '<=' | '=' | '!=';
  threshold: string | number;
  cooldown_minutes: number;
  notify_email?: string;
  notify_telegram?: string;
  enabled: boolean;
  last_triggered?: string;
  created_at: string;
  device_name?: string;
}

export interface AlertLog {
  id: string;
  alert_id: string;
  tenant_id: string;
  device_id: string;
  metric: string;
  operator: string;
  threshold: string | number;
  actual_value: string | number;
  triggered_at: string;
  alert_name?: string;
  device_name?: string;
}

export interface Invite {
  id: string;
  tenant_id: string;
  email: string;
  role: string;
  token: string;
  invited_by: string;
  invited_by_email?: string;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
  invite_url?: string;
}

export interface AuditLog {
  id: string;
  tenant_id: string;
  user_id?: string;
  user_email?: string;
  device_id?: string;
  device_name?: string;
  alert_id?: string;
  connection_id?: string;
  event_type: 'user_action' | 'device_action' | 'alert_action' | 'tenant_action' | 'mqtt_action' | 'system_action';
  action: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  metadata?: Record<string, any>;
  ip_address?: string;
  created_at: string;
}

export interface Tenant {
  id: string;
  name: string;
  plan: string;
  base_plan?: string | null;
  effective_plan?: string | null;
  is_active: boolean;
  suspended_at?: string | null;
  created_at: string;
  timezone?: string;
  logo_url?: string | null;
  trial_ends_at?: string | null;
  is_trial?: boolean;
  days_remaining?: number;
  device_count: number;
  user_count: number;
  alert_count: number;
}

export interface PlatformMetrics {
  total_tenants: number;
  total_users: number;
  total_devices: number;
  total_alerts: number;
  total_readings: number;
}

export interface Plan {
  id: string;
  name: string;
  slug: string;
  features: string[];
  max_devices: number | null;
  max_users: number | null;
  max_dashboards: number | null;
  max_data_streams?: number | null;
  price_monthly: number | string | null;
  currency: string | null;
  is_public: boolean;
  is_default: boolean;
  created_at: string;
}

export interface PlanPublic {
  id: string;
  name: string;
  slug: string;
  features: string[];
  is_default: boolean;
  max_devices: number | null;
  max_users: number | null;
  max_dashboards: number | null;
  max_data_streams?: number | null;
  price_monthly: number | string | null;
  currency: string | null;
}

export interface StreamUsageDevice {
  device_id: string;
  name: string;
  stream_count: number;
  streams: string[];
}

export interface StreamUsage {
  limit: number | null;
  total: number;
  devices: StreamUsageDevice[];
}

export interface TenantTrialStatus {
  trial_ends_at: string | null;
  days_remaining: number;
  is_trial: boolean;
  current_plan: string;
  effective_plan?: string;
  base_plan?: string;
  trial_plan_slug: string;
}

export interface PlanUpgradeResponse {
  status: 'redirect' | 'pending_admin_contact';
  payment_url?: string;
  tenant_id?: string;
  target_plan_slug?: string;
  support_email?: string;
}

export type PlatformSettings = Record<string, string>;

export interface TenantPlan {
  tenant_name: string;
  plan: string;
  plan_name: string;
  effective_plan?: string;
  base_plan?: string;
  is_trial?: boolean;
  features: string[];
  max_devices?: number | null;
  max_users?: number | null;
  max_dashboards?: number | null;
  max_data_streams?: number | null;
  trial_ends_at?: string | null;
}

export interface SuperAdminAccount {
  id: string;
  email: string;
  is_active: boolean;
  deactivated_at: string | null;
  created_at: string;
}

export interface TenantAdmin {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

export interface PlatformUsagePoint {
  date: string;
  tenants: number;
  users: number;
  devices: number;
  readings: number;
}

export interface PlatformUsage {
  series: PlatformUsagePoint[];
  per_tenant: { tenant_id: string; tenant_name: string; readings: number }[];
}

export interface PlatformAuditLog extends AuditLog {
  tenant_name?: string;
}

export interface PlatformAuditLogResponse {
  logs: PlatformAuditLog[];
  total: number;
}

export type TenantHealthStatus = 'healthy' | 'at_risk' | 'critical';

export interface TenantHealth {
  id: string;
  name: string;
  created_at: string;
  effective_plan: string;
  is_trial: boolean;
  trial_days_remaining: number;
  last_login_at?: string | null;
  active_users_7d: number;
  member_count: number;
  device_count: number;
  devices_offline: number;
  last_reading_at?: string | null;
  has_data_24h: boolean;
  user_dashboards: number;
  alert_count: number;
  failed_logins_7d: number;
  open_tickets: number;
  health_score: number;
  health_status: TenantHealthStatus;
  signals: string[];
}

// Dashboard Builder (Module 12)

export type DashboardWidgetType =
  | 'kpi'
  | 'timeseries'
  | 'consumption'
  | 'gauge'
  | 'tank'
  | 'gps_map'
  | 'state'
  | 'alerts'
  | 'status'
  | 'text';

export interface RuntimeCondition {
  id?: string;
  label: string;
  operator: '>' | '>=' | '<' | '<=' | '==' | '!=' | 'contains';
  value: string;
  color: string;
}

export interface WidgetConfig {
  title?: string;
  device_ids?: string[];
  metric?: string;
  metric_by_device?: Record<string, string>;
  chart_mode?: 'line' | 'threshold_trend' | 'step_state' | 'bucket_bar' | 'runtime_histogram';
  consumption_mode?: 'tank_drawdown' | 'cumulative_counter' | 'rate_total';
  refill_threshold?: number;
  counter_reset_threshold?: number;
  rate_time_unit?: 'hour' | 'minute' | 'second';
  tank_capacity?: number;
  min_valid?: number;
  max_valid?: number;
  max_gap_minutes?: number;
  max_daily_consumption?: number;
  condition_operator?: '>' | '>=' | '<' | '<=' | '==' | '!=' | 'contains';
  condition_value?: string;
  true_label?: string;
  false_label?: string;
  true_color?: string;
  false_color?: string;
  unknown_color?: string;
  runtime_states?: RuntimeCondition[];
  unmatched_runtime_behavior?: 'fallback' | 'unknown';
  bucket_size?: 'auto' | 'hour' | 'day';
  unit?: string;
  aggregation?: 'avg' | 'min' | 'max' | 'last';
  range_hours?: number;
  max?: number;
  thresholds?: { warn?: number; crit?: number };
  threshold_direction?: 'below' | 'above';
  text?: string;
  gauge_colors?: { low: string; midLow: string; midHigh: string; high: string };
  gauge_reverse?: boolean;
  tank_fill_color?: string;
  tank_low_threshold?: number;
  tank_high_threshold?: number;
  latitude_metric?: string;
  longitude_metric?: string;
  location_metric?: string;
  trail_color?: string;
}

export interface WidgetAlarm {
  id: string;
  tenant_id: string;
  dashboard_id: string;
  widget_id: string;
  device_id: string;
  metric: string;
  threshold_type: 'warn' | 'crit';
  threshold_value: number;
  direction: 'below' | 'above';
  value_at_alarm: number;
  status: 'active' | 'acknowledged' | 'cleared';
  acknowledged_by?: string;
  acknowledged_at?: string;
  device_name?: string;
  acknowledged_by_email?: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardWidget {
  id: string;
  type: DashboardWidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
  config: WidgetConfig;
}

export interface DashboardLayout {
  widgets: DashboardWidget[];
}

export interface Dashboard {
  id: string;
  name: string;
  is_default: boolean;
  layout: DashboardLayout;
  updated_at?: string;
  created_at?: string;
}

export interface DashboardSummary {
  id: string;
  name: string;
  is_default: boolean;
  updated_at?: string;
  widget_count: number;
}

export interface DashboardTemplate {
  id: string;
  name: string;
  industry?: string | null;
  description?: string | null;
  layout: DashboardLayout;
  is_builtin: boolean;
  tenant_id?: string | null;
  created_at?: string;
}

export interface WidgetDataPoint {
  time: string;
  device_id: string;
  value?: number;
  state?: string | null;
  duration_seconds?: number;
}

export interface WidgetLatestPoint {
  device_id: string;
  value: number | string | null;
  time?: string;
  state_duration_seconds?: number;
  age_seconds?: number | null;
  stale?: boolean;
  stable?: boolean;
}

export interface WidgetDataResponse {
  series: WidgetDataPoint[];
  latest: WidgetLatestPoint[];
}

export interface ConsumptionBucket {
  time: string;
  start_value: number | null;
  end_value: number | null;
  consumed: number;
  refill: number;
  refill_events: number;
  reset_events: number;
  point_count: number;
  coverage_start?: string | null;
  coverage_end?: string | null;
  anomaly?: boolean;
  anomaly_reasons?: string[];
  coverage_pct?: number;
  gap_minutes?: number;
}

export interface ConsumptionSummary {
  total_consumed: number;
  total_refill: number;
  refill_events: number;
  reset_events: number;
  start_value: number | null;
  end_value: number | null;
  coverage_start: string | null;
  coverage_end: string | null;
  point_count: number;
  is_complete: boolean;
  warnings?: string[];
}

export interface ConsumptionDataResponse {
  summary: ConsumptionSummary;
  buckets: ConsumptionBucket[];
}

export interface DeviceMetric {
  key: string;
  numeric: boolean;
}

export interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
  auto: boolean;
}

export interface OnboardingProgress {
  dismissed: boolean;
  dismissed_at?: string | null;
  overrides?: Record<string, boolean>;
  steps: OnboardingStep[];
  completed: number;
  total: number;
}

export type SupportTicketStatus = 'open' | 'in_progress' | 'waiting_for_customer' | 'resolved' | 'closed';
export type SupportTicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type SupportTicketCategory = 'bug' | 'feature_request' | 'billing' | 'device_data' | 'general';
export type SupportResolutionTag = 'dashboard' | 'device' | 'login' | 'billing' | 'sensor_data' | 'report' | 'mqtt' | 'account' | 'other';

export interface SupportTicket {
  id: string;
  ticket_number?: string | null;
  tenant_id: string;
  user_id: string;
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  assigned_to?: string | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at: string;
  requester_email?: string | null;
  requester_name?: string | null;
  tenant_name?: string | null;
  message_count?: number;
  last_message_at?: string | null;
}

export interface SupportTicketMessage {
  id: string;
  ticket_id: string;
  author_user_id?: string | null;
  is_superadmin: boolean;
  body: string;
  attachments?: string[];
  created_at: string;
  author_email?: string | null;
  author_name?: string | null;
}

export interface SupportTicketResolution {
  id?: string;
  ticket_id?: string;
  customer_issue?: string | null;
  root_cause: string;
  fix_applied: string;
  prevention_note?: string | null;
  internal_notes?: string | null;
  customer_resolution: string;
  tags?: SupportResolutionTag[];
  kb_candidate?: boolean;
  resolved_by?: string | null;
  resolved_by_email?: string | null;
  resolved_by_name?: string | null;
  created_at?: string | null;
}

export interface SupportTicketThread {
  ticket: SupportTicket;
  messages: SupportTicketMessage[];
  resolution?: SupportTicketResolution | null;
}

export interface SupportFeedback {
  id: string;
  tenant_id?: string | null;
  user_id?: string | null;
  rating: number;
  comment?: string | null;
  page?: string | null;
  created_at: string;
  tenant_name?: string | null;
  user_email?: string | null;
}
